import {
  Box,
  BoxProps,
  Container as MUIContainer,
  styled,
} from '@mui/material';
import SimpleBar from 'simplebar-react';

import { doNotForwardTheseProps } from '@/styles';

import { OverlayScroller, OverlayScrollerProps } from './OverlayScroller';
import { DesktopOnly } from './Responsive';

export const Form = styled((props: BoxProps) => (
  <Box component="form" {...props} />
))(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  alignSelf: 'center',
  gap: theme.spacing(3.5),
  width: '100%',

  [theme.breakpoints.up('md')]: {
    maxWidth: 360,
    gap: theme.spacing(4),
  },
  [theme.breakpoints.up('lg')]: {
    alignSelf: 'flex-start',
  },
}));

export const FullPageBackground = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const FullPageOverlayBackground = styled(Box, {
  shouldForwardProp: doNotForwardTheseProps('bgColor'),
})<{ bgColor?: string }>(({ theme, bgColor }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100dvh',
  width: '100dvw',
  backgroundColor: bgColor || theme.palette.grey.overlayOnLight,
  position: 'fixed',
  left: 0,
  top: 0,
  zIndex: theme.zIndex.drawer + 1,
}));

export const ScreenContent = styled(Box, {
  shouldForwardProp: doNotForwardTheseProps('bgColor'),
})<{ bgColor?: string }>(({ theme, bgColor }) => ({
  height: '100dvh',
  width: '100dvw',
  paddingTop: theme.layout.headerHeight.xs,
  paddingBottom: theme.spacing(6),
  overflowY: 'auto',
  ...(bgColor
    ? {
        backgroundColor: bgColor,
        position: 'fixed',
        left: 0,
        top: 0,
      }
    : {
        backgroundColor: 'transparent',
      }),
}));

export const Container = styled(MUIContainer, {
  shouldForwardProp: doNotForwardTheseProps('center', 'noPadding'),
})<{ noPadding?: boolean; center?: boolean }>(
  ({ theme, noPadding, center }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    minHeight: '100%',
    '> *': {
      flexShrink: 0,
    },
    ...(center
      ? {
          [theme.breakpoints.up('sm')]: {
            justifyContent: 'center',
          },
        }
      : {}),
    ...(noPadding && { padding: 0 }),
  }),
);

export const ScreenFooter = styled(Box)(() => ({
  position: 'fixed',
  width: '100%',
  bottom: 0,
  left: 0,
}));

export const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  color: theme.palette.common.white,
  height: '100%',
  width: '100%',
  left: 0,
  top: 0,
  zIndex: 1,
}));

export const BottomNoteBackground = styled(Box)(({ theme }) => ({
  borderTopLeftRadius: 64,
  borderTopRightRadius: 64,
  background: theme.palette.grey[200],
  padding: `${theme.spacing(4)} ${theme.spacing(2)} ${theme.spacing(6)}`,
}));

export const FullPageCenter = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  height: '100dvh',
  width: '100dvw',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const DrawerHeader = styled(DesktopOnly)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  flexShrink: 0,
  padding: theme.spacing(3),
}));

export const DrawerContent = styled((props: BoxProps) => (
  <Box className="DrawerContent" {...props} />
))(({ theme }) => ({
  padding: theme.spacing(2),
  paddingTop: theme.layout.headerHeight.xs,
  paddingBottom: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minHeight: 0,

  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(3),
  },
}));

export const DrawerContentScroller = styled((props: OverlayScrollerProps) => (
  <OverlayScroller className="DrawerContentScroller" {...props} />
))(({ theme }) => ({
  padding: theme.spacing(2),
  paddingTop: theme.layout.headerHeight.xs,
  paddingBottom: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minHeight: 0,

  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(3),
    paddingTop: 0,
  },
}));

export const DrawerAction = styled(
  (props: React.HTMLAttributes<HTMLDivElement>) => (
    <div className="FormAction" {...props} />
  ),
)<{
  align?: 'stretch' | 'center' | 'left' | 'right';
}>(({ theme, align = 'left' }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  width: '100%',
  maxWidth: 360,

  ...(align === 'left'
    ? {
        justifyContent: 'flex-start',
        '.MuiButton-contained': {
          flex: 1,
        },
      }
    : align === 'right'
      ? {
          justifyContent: 'flex-end',
          '.MuiButton-contained': {
            flex: 1,
          },
        }
      : align === 'center'
        ? {
            justifyContent: 'center',
            '.MuiButton-contained': {
              flex: 1,
            },
          }
        : align === 'stretch'
          ? {
              '> *': {
                flex: 1,
              },
            }
          : {}),
}));

export const ActionBar = styled(Box, {
  shouldForwardProp: doNotForwardTheseProps('growContainedOnly'),
})<{ growContainedOnly?: boolean }>(({ theme, growContainedOnly }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',

  '.MuiButton-root': {
    minWidth: 127,
  },

  ...(growContainedOnly
    ? {
        '> .MuiButton-contained': {
          flex: 1,
        },
      }
    : {
        '> *': {
          flex: 1,
        },
      }),

  [theme.breakpoints.up('sm')]: {
    '.MuiButton-root': {
      minWidth: 87,
    },
  },
}));

export const StyledSimpleBar = styled(SimpleBar)(({ theme }) => ({
  '.simplebar-scrollbar': {
    '::before': {
      backgroundColor: theme.palette.grey[500],
    },
  },
}));
